/**
 * Accepted-but-not-yet-reported import jobs (pure).
 *
 * `POST /imports/sources/:id/sync` answers 202 with the job it queued. The
 * live panel shows that job the instant the answer is read, as queued, instead
 * of waiting for the next `/imports/sync/status` round-trip: an import the
 * user just started must never appear late, or not at all when it finishes
 * between two polls.
 *
 * Kept out of `monitoringStore.ts` so consumers (and tests that mock the store)
 * use the same rules without the store itself.
 */

import type { ActiveSync, SyncStatus } from '../types'

export interface AcceptedImportJob {
  jobId: string
  sourceId: string | null
  sourceName: string
  type: ActiveSync['type']
  /** Server instant the job was queued (ISO), from the 202 body. */
  startedAt: string
  /** Local epoch ms the 202 was read: a snapshot requested before it cannot judge the job. */
  acceptedAt: number
}

export type AcceptedImportJobs = Readonly<Record<string, AcceptedImportJob>>

/**
 * Drop every accepted job that a snapshot requested AFTER its acceptance has
 * judged: either the snapshot lists it (the server row replaces ours) or it has
 * already left the active set (finished — the panel's observer reads how).
 */
export function pruneAcceptedJobs(
  accepted: AcceptedImportJobs,
  requestedAt: number,
): Record<string, AcceptedImportJob> {
  const kept: Record<string, AcceptedImportJob> = {}
  for (const job of Object.values(accepted)) {
    if (job.acceptedAt > requestedAt) kept[job.jobId] = job
  }
  return kept
}

/**
 * The live list the panel renders: every server `pending`/`processing` job,
 * then each accepted job the server has not reported yet, as `pending`.
 */
export function selectLiveSyncs(
  status: SyncStatus | null,
  accepted: AcceptedImportJobs | undefined,
): ActiveSync[] {
  const server = (status?.activeSyncs ?? []).filter(
    (sync) => sync.status === 'processing' || sync.status === 'pending',
  )
  const known = new Set(server.map((sync) => sync.jobId))
  const optimistic: ActiveSync[] = Object.values(accepted ?? {})
    // Defensive: anything that is not a job record (a rehydrated older shape)
    // must never become a ghost row.
    .filter((job) => typeof job?.jobId === 'string' && job.jobId.length > 0)
    .filter((job) => !known.has(job.jobId))
    .map((job) => ({
      jobId: job.jobId,
      sourceId: job.sourceId,
      sourceName: job.sourceName,
      type: job.type,
      status: 'pending',
      queueName: '',
      priority: 0,
      progress: null,
      recordsProcessed: 0,
      startedAt: job.startedAt,
      createdAt: job.startedAt,
      currentRetry: 0,
      maxRetries: 0,
    }))
  return [...server, ...optimistic]
}
